/**
 * The planet: one rounded hexagon block per tile, as in the prototype. Each block is a flat top, a
 * rounded edge in a few steps, and a wall going down out of sight (tiles.ts writes them). The tiles
 * sit in chunks (chunks.ts), one mesh each, so what the camera can't see isn't drawn. When tiles
 * change, only their vertices are rewritten (buffer update ranges). The look decides the material:
 * today's lit one with shadows, or the stylized surface shader.
 */
import * as THREE from 'three';

import type { Look } from '../style/look';
import { surfaceMaterial } from '../style/surfaceMaterial';
import { chunksFor, overHorizon, type ChunkLayout } from './chunks';
import { layoutFor, type Layout } from './layout';
import { writeTile, type TileBuffers, type TilePaint } from './tiles';
import { KIND_HEIGHT, radiusOf, STEP, type PlanetView } from './view';

const POP_MS = 520;

export interface Planet {
  /** The radius of the planet shown now (it eases after a growth). */
  radius(): number;
  /** Show a planet: everything is rebuilt when the size changed, else only the tiles that did. Returns the changed tiles (null: all). */
  show(view: PlanetView, now: number): number[] | null;
  /** Mark the tile I stand on: a lighter top and a rim in `tint`, the colour of the tool I hold. */
  setHere(tile: number, tint: string, now: number): void;
  /** Start a tile's little bounce. */
  pop(tile: number, now: number): void;
  /** Advance the bounces; returns whether any is still going. */
  animate(now: number): boolean;
  /** Before a frame: leave out the chunks beyond the horizon for this camera. */
  cull(camera: THREE.Camera): void;
  /** The stylized surface's painted detail on or off (a quality tier). */
  setDetail(on: boolean): void;
  /** The top of a tile above the planet's radius: its level plus its own height. */
  tileTop(tile: number): number;
  shown(): PlanetView | null;
}

interface Chunk {
  mesh: THREE.Mesh;
  attributes: THREE.BufferAttribute[];
  buffers: TileBuffers;
  dirty: boolean;
}

const classicMaterial = (): THREE.Material =>
  new THREE.MeshPhysicalMaterial({
    vertexColors: true,
    roughness: 0.82,
    metalness: 0,
    sheen: 0.4,
    sheenRoughness: 0.6,
    sheenColor: new THREE.Color(0xe3eeff),
  });

/** Whether a tile looks different between two views (kind, height, group, drawn or not). */
function differs(a: PlanetView, b: PlanetView, i: number): boolean {
  return (
    a.kind[i] !== b.kind[i] ||
    a.elev[i] !== b.elev[i] ||
    a.groups[i] !== b.groups[i] ||
    (a.shown?.[i] ?? 1) !== (b.shown?.[i] ?? 1) ||
    (a.drawn?.[i] ?? 0) !== (b.drawn?.[i] ?? 0) ||
    (a.shattered?.[i] ?? 0) !== (b.shattered?.[i] ?? 0)
  );
}

const _camera = new THREE.Vector3();

/** `detail`: the stylized surface's painted detail (off on the low quality tier). */
export function createPlanet(parent: THREE.Group, look: Look = 'classic', detail = true): Planet {
  const stylized = look === 'stylized';
  const material = stylized ? surfaceMaterial(detail) : classicMaterial();
  let layout: Layout | null = null;
  let chunking: ChunkLayout | null = null;
  let chunks: Chunk[] = [];
  let shown: PlanetView | null = null;
  let tint = '';
  let writeNow = 0;
  const pops = new Map<number, number>();
  /** The tiles to rewrite, reused every frame. */
  const touched: number[] = [];
  /** Changed tiles and, for the stylized look, their neighbours (corners and shores follow them). */
  const rewrite = new Set<number>();

  const paint: TilePaint = {
    look,
    here: -1,
    tint: new THREE.Color(),
    top(i, kind) {
      let h = (shown?.elev[i] ?? 0) * STEP + (KIND_HEIGHT[kind] ?? 0);
      const started = pops.get(i);
      if (started !== undefined) {
        const p = Math.min(1, (writeNow - started) / POP_MS);
        if (p < 1) h += Math.sin(p * Math.PI) * 0.09;
      }
      return h;
    },
  };

  /** One mesh per chunk, sized for its tiles, all sharing the material. */
  const build = (grid: PlanetView['grid']): void => {
    for (const chunk of chunks) {
      parent.remove(chunk.mesh);
      chunk.mesh.geometry.dispose();
    }
    layout = layoutFor(grid);
    chunking = chunksFor(grid, layout);
    chunks = [];
    for (let k = 0; k < chunking.count; k++) {
      const nv = chunking.vertices[k] ?? 0;
      const buffers: TileBuffers = {
        position: new Float32Array(nv * 3),
        normal: new Float32Array(nv * 3),
        color: new Float32Array(nv * 3),
        tile: stylized ? new Float32Array(nv * 4) : null,
        glow: stylized ? new Float32Array(nv) : null,
        column: stylized ? new Float32Array(nv * 4) : null,
      };
      const position = new THREE.BufferAttribute(buffers.position, 3);
      const normal = new THREE.BufferAttribute(buffers.normal, 3);
      const color = new THREE.BufferAttribute(buffers.color, 3);
      const tile = buffers.tile ? new THREE.BufferAttribute(buffers.tile, 4) : null;
      const glow = buffers.glow ? new THREE.BufferAttribute(buffers.glow, 1) : null;
      const column = buffers.column ? new THREE.BufferAttribute(buffers.column, 4) : null;
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', position);
      geometry.setAttribute('normal', normal);
      geometry.setAttribute('color', color);
      if (tile) geometry.setAttribute('aTile', tile);
      if (glow) geometry.setAttribute('aGlow', glow);
      if (column) geometry.setAttribute('aColumn', column);
      geometry.setIndex(new THREE.BufferAttribute(chunking.index[k] ?? new Uint32Array(), 1));
      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = !stylized;
      mesh.receiveShadow = !stylized;
      parent.add(mesh);
      chunks.push({
        mesh,
        attributes:
          tile && glow && column
            ? [position, normal, color, tile, glow, column]
            : [position, normal, color],
        buffers,
        dirty: false,
      });
    }
  };

  const write = (list: Iterable<number> | null, now: number): void => {
    if (!layout || !chunking || !shown) return;
    const view = shown;
    writeNow = now;
    if (list) {
      for (const i of list) {
        if (i < 0 || i >= view.grid.count) continue;
        const chunk = chunks[chunking.chunkOf[i] ?? 0];
        if (!chunk) continue;
        const v0 = chunking.vertexOf[i] ?? 0;
        writeTile(view, layout, chunk.buffers, paint, i, v0);
        const count = (layout.off[i + 1] ?? 0) - (layout.off[i] ?? 0);
        for (const a of chunk.attributes) a.addUpdateRange(v0 * a.itemSize, count * a.itemSize);
        chunk.dirty = true;
      }
    } else {
      for (const chunk of chunks) {
        for (const a of chunk.attributes) a.clearUpdateRanges();
        chunk.dirty = true;
      }
      for (let i = 0; i < view.grid.count; i++) {
        const chunk = chunks[chunking.chunkOf[i] ?? 0];
        if (chunk) writeTile(view, layout, chunk.buffers, paint, i, chunking.vertexOf[i] ?? 0);
      }
    }
    for (const chunk of chunks) {
      if (!chunk.dirty) continue;
      for (const a of chunk.attributes) a.needsUpdate = true;
      chunk.dirty = false;
    }
  };

  return {
    radius: () => (shown ? radiusOf(shown.grid.subdivisions) : radiusOf(3)),
    shown: () => shown,
    show(view, now) {
      const sizeChanged = shown?.grid.subdivisions !== view.grid.subdivisions || !chunking;
      let changed: number[] | null = null;
      if (!sizeChanged && shown) {
        changed = [];
        for (let i = 0; i < view.grid.count; i++) if (differs(view, shown, i)) changed.push(i);
      }
      shown = {
        grid: view.grid,
        kind: view.kind.slice(),
        elev: view.elev.slice(),
        groups: view.groups.slice(),
        ...(view.shown && { shown: view.shown.slice() }),
        ...(view.drawn && { drawn: view.drawn.slice() }),
        ...(view.shattered && { shattered: view.shattered.slice() }),
        ...(view.layers && { layers: view.layers }),
      };
      if (sizeChanged) {
        build(view.grid);
        if (paint.here >= view.grid.count) paint.here = -1;
        pops.clear();
        write(null, now);
        for (const chunk of chunks) chunk.mesh.geometry.computeBoundingSphere();
        return null;
      }
      if (changed?.length) {
        rewrite.clear();
        for (const i of changed) {
          rewrite.add(i);
          if (stylized) for (const j of view.grid.neighbors[i] ?? []) rewrite.add(j);
        }
        write(rewrite.size < view.grid.count / 3 ? rewrite : null, now);
      }
      return changed;
    },
    setHere(tile, color, now) {
      if ((tile === paint.here && color === tint) || !chunking) return;
      touched.length = 0;
      if (paint.here >= 0) touched.push(paint.here);
      if (tile >= 0 && tile !== paint.here) touched.push(tile);
      paint.here = tile;
      tint = color;
      paint.tint.set(color);
      write(touched, now);
    },
    pop(tile, now) {
      pops.set(tile, now);
    },
    animate(now) {
      if (pops.size === 0) return false;
      touched.length = 0;
      for (const [i, t0] of pops) {
        touched.push(i);
        if (now - t0 > POP_MS + 100) pops.delete(i);
      }
      write(touched, now);
      return pops.size > 0;
    },
    cull(camera) {
      if (!chunking || !shown) return;
      parent.updateMatrixWorld();
      parent.worldToLocal(camera.getWorldPosition(_camera));
      const radius = radiusOf(shown.grid.subdivisions);
      for (let k = 0; k < chunks.length; k++) {
        const chunk = chunks[k];
        if (chunk)
          chunk.mesh.visible = !overHorizon(chunking, k, _camera.x, _camera.y, _camera.z, radius);
      }
    },
    setDetail(on) {
      if (!(material instanceof THREE.ShaderMaterial)) return;
      const has = 'SURFACE_DETAIL' in material.defines;
      if (has === on) return;
      if (on) material.defines['SURFACE_DETAIL'] = '';
      else delete material.defines['SURFACE_DETAIL'];
      material.needsUpdate = true;
    },
    tileTop(tile) {
      if (!shown) return 0;
      return (shown.elev[tile] ?? 0) * STEP + (KIND_HEIGHT[shown.kind[tile] ?? 0] ?? 0);
    },
  };
}
